/**
 * Catalog image accessors — delegate to catalogImageLookup.v1.ts (two static configs).
 * Docs: docs/docsForUI/CATALOG_STATIC_IMAGES_V1.md
 */
import { catalogImageForTemplateKey } from './catalogImageLookup.v1';
import {
  getCatalogTestConfig,
  getCatalogTestConfigByKey,
  staticManifestTestsForDifficulty,
  type TargetManifestSlot,
} from './testGroupAndTestConfig.v1';
import type { Difficulty, Topic } from '../Pages/DataBaseV3/Questions/QuestionsConstant';
import type { GroupType } from './types';

export type CatalogStaticImage = {
  imageUrl: string;
  imageStoragePath: string;
};

export const CATALOG_STATIC_MANIFEST_VERSION = 'catalog-static.v1';

export const CATALOG_STATIC_IMAGE_BUCKET =
  process.env.REACT_APP_CATALOG_IMAGE_STORAGE_BUCKET?.trim() || 'vehiwheelprod';

export const CATALOG_MIRROR_IMAGE_ROOT = 'images/catalog';

/** Used when uploading a new JPEG (writes to an existing config path only). */
export function catalogImageUrlFromStoragePath(path: string, token?: string): string {
  const base = `https://firebasestorage.googleapis.com/v0/b/${encodeURIComponent(
    CATALOG_STATIC_IMAGE_BUCKET
  )}/o/${encodeURIComponent(path)}?alt=media`;
  return token ? `${base}&token=${token}` : base;
}

export function getStaticTestGroupImage(templateKey: string): CatalogStaticImage | undefined {
  return catalogImageForTemplateKey(templateKey);
}

export function getStaticTestImageForTemplateKey(
  templateKey: string,
  _parentGroupType?: GroupType,
  _childTemplateKey?: string
): CatalogStaticImage | undefined {
  return catalogImageForTemplateKey(templateKey);
}

export function getStaticTopicTestImage(
  _topic: string,
  _slotIndex: number,
  _difficulty?: Difficulty
): CatalogStaticImage {
  throw new Error(
    'getStaticTopicTestImage is removed — pass templateKey to getStaticTestImageForTemplateKey / catalogImageForTemplateKey'
  );
}

export function getStaticManifestSlotImage(
  slot: Pick<TargetManifestSlot, 'topic' | 'slotIndex' | 'difficulty' | 'templateKey'>
): CatalogStaticImage {
  const image = catalogImageForTemplateKey(slot.templateKey);
  if (!image) {
    throw new Error(`No catalog image for templateKey ${slot.templateKey}`);
  }
  return image;
}

export function getStaticCrossStampTestImage(
  _parentGroupType: GroupType,
  _childTemplateKey: string
): CatalogStaticImage {
  throw new Error(
    'getStaticCrossStampTestImage is removed — use getStaticTestImageForTemplateKey(compositeTemplateKey)'
  );
}

export function topicAndSlotFromManifestTemplateKey(
  templateKey: string
): { topic: string; slotIndex: number; difficulty?: Difficulty } | null {
  const key = String(templateKey || '').trim();
  const byDifficulties =
    /^difficulty\.(soft|medium|hard)\.topic\.([a-z0-9][a-z0-9._-]*)\.slot\.(\d+)$/i.exec(key);
  if (byDifficulties) {
    return {
      difficulty: byDifficulties[1] as Difficulty,
      topic: byDifficulties[2]!,
      slotIndex: Number(byDifficulties[3]),
    };
  }
  const byTopics =
    /^topic\.([a-z0-9][a-z0-9._-]*)\.difficulty\.(soft|medium|hard)\.slot\.(\d+)$/i.exec(key);
  if (byTopics) {
    return {
      topic: byTopics[1]!,
      difficulty: byTopics[2] as Difficulty,
      slotIndex: Number(byTopics[3]),
    };
  }
  return null;
}

export type CatalogStaticManifestTestSlot = TargetManifestSlot & {
  imageFileName: string;
  image: CatalogStaticImage;
};

export const STATIC_MANIFEST_TEST_SLOTS: CatalogStaticManifestTestSlot[] = (
  ['soft', 'medium', 'hard'] as const
).flatMap((d) =>
  staticManifestTestsForDifficulty(d).map((s) => ({
    orderIndex: s.orderIndex,
    difficulty: s.difficulty,
    topic: s.topic,
    slotIndex: s.slotIndex,
    testTitle: s.testTitle,
    templateKey: s.templateKey,
    imageFileName: s.imageFileName,
    image: s.image,
  }))
);

export {
  staticManifestTestsForDifficulty,
  catalogImageForTemplateKey,
  getCatalogTestConfigByKey,
};

export function staticManifestTestsForGroupTemplateKey(groupTemplateKey: string): ReturnType<
  typeof staticManifestTestsForDifficulty
> {
  if (groupTemplateKey === 'difficulty.soft') return staticManifestTestsForDifficulty('soft');
  if (groupTemplateKey === 'difficulty.medium') return staticManifestTestsForDifficulty('medium');
  if (groupTemplateKey === 'difficulty.hard') return staticManifestTestsForDifficulty('hard');
  return [];
}

export function catalogStaticImageIsReadyForPreview(image: CatalogStaticImage | undefined): boolean {
  if (!image?.imageUrl?.trim()) return false;
  return image.imageUrl.includes('token=');
}

export function previewImageForDifficultyGroup(difficulty: string): CatalogStaticImage | undefined {
  return catalogImageForTemplateKey(`difficulty.${difficulty}`);
}

export function previewImageForTopicGroup(topic: string): CatalogStaticImage | undefined {
  return catalogImageForTemplateKey(`topic.${topic}`);
}

export function previewImageForCatalogTest(
  topic: string,
  slotIndex: number,
  difficulty: Difficulty,
  templateKey: string
): CatalogStaticImage {
  const image = catalogImageForTemplateKey(templateKey);
  if (!image) {
    throw new Error(`No catalog image for ${templateKey} (${difficulty} ${topic} slot ${slotIndex})`);
  }
  return image;
}

/**
 * Thumbnail URL for admin grids: only fields stored on the Firestore document
 * (`testgroup_test_{state}`). No static-config fallback.
 */
export function resolveCatalogThumbUrl(row: {
  imageUrl?: unknown;
  imageThumbUrl?: unknown;
}): string | null {
  if (typeof row.imageUrl === 'string' && row.imageUrl.trim()) return row.imageUrl.trim();
  if (typeof row.imageThumbUrl === 'string' && row.imageThumbUrl.trim()) {
    return row.imageThumbUrl.trim();
  }
  return null;
}

export function resolveCatalogImageFields(
  existing: { imageUrl?: unknown; imageStoragePath?: unknown } | null | undefined,
  fallback: CatalogStaticImage | undefined
): { imageUrl?: string; imageStoragePath?: string } {
  const storedPath =
    typeof existing?.imageStoragePath === 'string' && existing.imageStoragePath.trim()
      ? existing.imageStoragePath.trim()
      : '';
  const configPath = fallback?.imageStoragePath?.trim() ?? '';
  const legacyPath =
    configPath && storedPath && storedPath !== configPath;

  const imageUrl = legacyPath
    ? fallback?.imageUrl
    : typeof existing?.imageUrl === 'string' && existing.imageUrl.trim()
      ? existing.imageUrl.trim()
      : fallback?.imageUrl;
  const imageStoragePath = legacyPath
    ? fallback?.imageStoragePath
    : storedPath || fallback?.imageStoragePath;
  return {
    ...(imageUrl ? { imageUrl } : {}),
    ...(imageStoragePath ? { imageStoragePath } : {}),
  };
}
